import { UserRole, ShopWebsiteArea } from '@/api';
import { RouteLocationNormalized, RouteRecordRaw, RouteMeta } from 'vue-router';

export interface RouteMetaExtended extends RouteMeta {
    allowAnonymous?: boolean;
    role?: UserRole;
    area: ShopWebsiteArea;
    breadcrumb: { text: string, pathName?: RouteNames }[]
}

export enum RouteNames {
    FRONTPAGE = 'FRONTPAGE',

    AUTH = 'AUTH',
    AUTH_LOGIN = 'AUTH_LOGIN',
    AUTH_REGISTER = 'AUTH_REGISTER',
    AUTH_FORGOT_PASSWORD = 'AUTH_FORGOT_PASSWORD',
    AUTH_RESET_PASSWORD = 'AUTH_RESET_PASSWORD',

    FORMS = 'FORMS',
    FORMS_OVERVIEW = 'FORMS_OVERVIEW',
    FORMS_SOLE_PROPRIETORSHIP = 'FORMS_SOLE_PROPRIETORSHIP',
    FORMS_APS = 'FORMS_APS',
    FORMS_AS = 'FORMS_AS',

    DASHBOARD = 'DASHBOARD',

    CASES = 'CASES',
    CASES_ACTIVE = 'CASES_ACTIVE',
    CASES_CLOSED = 'CASES_CLOSED',
    CASES_CASE = 'CASES_CASE',
    PARTNERS = 'PARTNERS',

    USER_PROFILE = 'USER_PROFILE',
    AUTH_VERIFY_EMAIL = 'AUTH_VERIFY_EMAIL',

    DASHBOARD_FORMS_OVERVIEW = 'DASHBOARD_FORMS_OVERVIEW',
    DASHBOARD_FORMS_SOLE_PROPRIETORSHIP = 'DASHBOARD_FORMS_SOLE_PROPRIETORSHIP',
    DASHBOARD_FORMS_APS = 'DASHBOARD_FORMS_APS',
    DASHBOARD_FORMS_AS = 'DASHBOARD_FORMS_AS',
    CASES_CASE_STATUS = 'CASES_CASE_STATUS',
    CASES_CASE_INFO = 'CASES_CASE_INFO',
    CASES_CASE_FILES = 'CASES_CASE_FILES',
    CASES_CASE_EVENTS = 'CASES_CASE_EVENTS',
    CASES_CASE_SELSKABSSTIFTELSE = 'CASES_CASE_SELSKABSSTIFTELSE',
    CASES_CASE_EJERBOG = 'CASES_CASE_EJERBOG',
    CASES_CASE_STIFTELSESDOKUMENT = 'CASES_CASE_STIFTELSESDOKUMENT',
    CASES_CASE_SELSKABSMAPPE = 'CASES_CASE_SELSKABSMAPPE',
    CASES_CASE_KYC_VERIFICERING = 'CASES_CASE_KYC_VERIFICERING',
    CASES_CASE_IDENTIFIKATION = 'CASES_CASE_IDENTIFIKATION',
    CASES_CASE_IDENTIFIKATION_PUBLIC = 'CASES_CASE_IDENTIFIKATION_PUBLIC',
    CASES_CASE_PARTICIPANT_IDENTIFIKATION = 'CASES_CASE_PARTICIPANT_IDENTIFIKATION',
    CASES_CASE_PARTICIPANT_IDENTIFIKATION_PUBLIC = 'CASES_CASE_PARTICIPANT_IDENTIFIKATION_PUBLIC',
    KYC_PUBLIC = 'KYC_PUBLIC',
}

const createPath = (...paths: string[]) => {
    return `/${paths.join('/')}`;
};

export const routes: Array<RouteRecordRaw> = [
    {
        path: '/kyc', name: RouteNames.KYC_PUBLIC,
        component: () => import('@/core/pages/kyc/KycPublic.vue'),
        props: (route: RouteLocationNormalized) => ({ token: new URLSearchParams(route.hash.slice(1)).get('token') ?? '' }),
        meta: { allowAnonymous: true, area: ShopWebsiteArea.Auth },
    },
    {
        path: createPath(''),
        name: RouteNames.FRONTPAGE,
        component: () => import('@/pages/Frontpage.vue'),
        meta: {
            allowAnonymous: true,
            area: ShopWebsiteArea.Frontpage,
        } as RouteMetaExtended,
    },
    {
        path: createPath('dashboard'),
        name: RouteNames.DASHBOARD,
        component: () => import('@/pages/dashboard/Index.vue'),
        meta: {
            allowAnonymous: false,
            area: ShopWebsiteArea.Dashboard,
        } as RouteMetaExtended,
        redirect: {
            name: RouteNames.CASES,
        },
        children: [
            {
                path: createPath('cases'),
                name: RouteNames.CASES,
                component: () => import('@/pages/dashboard/cases/Index.vue'),
                meta: {
                    allowAnonymous: false,
                    area: ShopWebsiteArea.Dashboard,
                } as RouteMetaExtended,
                redirect: {
                    name: RouteNames.CASES_ACTIVE,
                },
                children: [
                    {
                        path: createPath('cases', 'active'),
                        name: RouteNames.CASES_ACTIVE,
                        component: () => import('@/pages/dashboard/cases/Active.vue'),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('cases', 'closed'),
                        name: RouteNames.CASES_CLOSED,
                        component: () => import('@/pages/dashboard/cases/Closed.vue'),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                ],
            },
            {
                path: createPath('samarbejdspartnere'),
                name: RouteNames.PARTNERS,
                component: () => import('@/pages/dashboard/partners/Index.vue'),
                meta: {
                    allowAnonymous: false,
                    area: ShopWebsiteArea.Dashboard,
                } as RouteMetaExtended,
            },
            {
                path: createPath('case', ':caseId'),
                name: RouteNames.CASES_CASE,
                component: () => import('@/core/pages/dashboard/cases/case/Index.vue'),
                redirect: { name: RouteNames.CASES_CASE_STATUS },
                props: (route: RouteLocationNormalized) => ({
                    caseId: route.params.caseId,
                }),
                meta: {
                    allowAnonymous: false,
                    area: ShopWebsiteArea.Dashboard,
                } as RouteMetaExtended,
                children: [
                    {
                        path: createPath('case', ':caseId', 'status'),
                        name: RouteNames.CASES_CASE_STATUS,
                        component: () => import('@/core/pages/dashboard/cases/case/Status.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('case', ':caseId', 'info'),
                        name: RouteNames.CASES_CASE_INFO,
                        component: () => import('@/core/pages/dashboard/cases/case/Info.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('case', ':caseId', 'files'),
                        name: RouteNames.CASES_CASE_FILES,
                        component: () => import('@/core/pages/dashboard/cases/case/Files.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('case', ':caseId', 'ejerbog'),
                        name: RouteNames.CASES_CASE_EJERBOG,
                        component: () => import('@/core/pages/dashboard/cases/case/Ejerbog.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('case', ':caseId', 'stiftelsesdokument'),
                        name: RouteNames.CASES_CASE_STIFTELSESDOKUMENT,
                        component: () => import('@/core/pages/dashboard/cases/case/StiftelsesDokument.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('case', ':caseId', 'selskabsmappe'),
                        name: RouteNames.CASES_CASE_SELSKABSMAPPE,
                        component: () => import('@/core/pages/dashboard/cases/case/Selskabsmappe.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('case', ':caseId', 'kyc-verificering'),
                        name: RouteNames.CASES_CASE_KYC_VERIFICERING,
                        component: () => import('@/core/pages/dashboard/cases/case/KycVerificering.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('case', ':caseId', 'identifikation', ':casePersonId'),
                        name: RouteNames.CASES_CASE_IDENTIFIKATION,
                        component: () => import('@/core/pages/dashboard/cases/case/Identifikation.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                            casePersonId: route.params.casePersonId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                    {
                        path: createPath('case', ':caseId', 'identifikation', 'participant', ':caseParticipantId'),
                        name: RouteNames.CASES_CASE_PARTICIPANT_IDENTIFIKATION,
                        component: () => import('@/core/pages/dashboard/cases/case/Identifikation.vue'),
                        props: (route: RouteLocationNormalized) => ({
                            caseId: route.params.caseId,
                            caseParticipantId: route.params.caseParticipantId,
                        }),
                        meta: {
                            allowAnonymous: false,
                            area: ShopWebsiteArea.Dashboard,
                        } as RouteMetaExtended,
                    },
                ],
            },
            {

                path: createPath('user', 'profile'),
                name: RouteNames.USER_PROFILE,
                component: () => import('@/pages/dashboard/profile/Profile.vue'),
                meta: {
                    allowAnonymous: false,
                    area: ShopWebsiteArea.Dashboard,
                } as RouteMetaExtended,
            },
            {
                path: createPath('user', 'verify-email'),
                name: RouteNames.AUTH_VERIFY_EMAIL,
                component: () => import('@/pages/dashboard/profile/VerifyEmail.vue'),
                props: (route: RouteLocationNormalized) => ({
                    token: route.query.token,
                }),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Auth,
                },
            },
            {
                path: createPath('dashboard', 'forms', 'overview'),
                name: RouteNames.DASHBOARD_FORMS_OVERVIEW,
                component: () => import('@/core/pages/dashboard/forms/Overview.vue'),
                meta: {
                    allowAnonymous: false,
                    area: ShopWebsiteArea.Dashboard,
                } as RouteMetaExtended,
            },
            {
                path: createPath('dashboard', 'forms', 'sole-proprietorship', ':caseId?'),
                name: RouteNames.DASHBOARD_FORMS_SOLE_PROPRIETORSHIP,
                component: () => import('@/core/pages/dashboard/forms/enkeltmandsvirksomhed/Index.vue'),
                props: (route: RouteLocationNormalized) => ({
                    caseId: route.params.caseId,
                }),
                meta: {
                    allowAnonymous: false,
                    area: ShopWebsiteArea.Dashboard,
                } as RouteMetaExtended,
            },
            {
                path: createPath('dashboard', 'forms', 'aps', ':caseId?'),
                name: RouteNames.DASHBOARD_FORMS_APS,
                component: () => import('@/core/pages/dashboard/forms/aps/Index.vue'),
                props: (route: RouteLocationNormalized) => ({
                    caseId: route.params.caseId,
                }),
                meta: {
                    allowAnonymous: false,
                    area: ShopWebsiteArea.Dashboard,
                } as RouteMetaExtended,
            },
            {
                path: createPath('dashboard', 'forms', 'aktieselskab', ':caseId?'),
                name: RouteNames.DASHBOARD_FORMS_AS,
                component: () => import('@/core/pages/dashboard/forms/aktieselskab/Index.vue'),
                props: (route: RouteLocationNormalized) => ({
                    caseId: route.params.caseId,
                }),
                meta: {
                    allowAnonymous: false,
                    area: ShopWebsiteArea.Dashboard,
                } as RouteMetaExtended,
            },
        ],
    },
    {
        path: createPath('auth'),
        name: RouteNames.AUTH,
        component: () => import('@/pages/login/Index.vue'),
        meta: {
            allowAnonymous: true,
            area: ShopWebsiteArea.Auth,
        },
        children: [
            {
                path: createPath('auth', 'login'),
                name: RouteNames.AUTH_LOGIN,
                component: () => import('@/pages/login/Login.vue'),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Auth,
                },
            },
            {
                path: createPath('auth', 'register'),
                name: RouteNames.AUTH_REGISTER,
                component: () => import('@/pages/login/Register.vue'),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Auth,
                },
            },
            {
                path: createPath('auth', 'forgot-password'),
                name: RouteNames.AUTH_FORGOT_PASSWORD,
                component: () => import('@/pages/login/ForgotPassword.vue'),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Auth,
                },
            },
            {
                path: createPath('auth', 'reset-password'),
                name: RouteNames.AUTH_RESET_PASSWORD,
                component: () => import('@/pages/login/ResetPassword.vue'),
                props: (route: RouteLocationNormalized) => ({
                    token: route.query.token,
                }),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Auth,
                },
            },
        ],
    },
    {
        path: createPath('forms'),
        name: RouteNames.FORMS,
        component: () => import('@/core/pages/forms/Index.vue'),
        redirect: {
            name: RouteNames.FORMS_OVERVIEW,
        },
        meta: {
            allowAnonymous: true,
            area: ShopWebsiteArea.Forms,
        },
        children: [
            {
                path: createPath('forms', 'overview'),
                name: RouteNames.FORMS_OVERVIEW,
                component: () => import('@/core/pages/forms/Overview.vue'),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Forms,
                },
            },
            {
                path: createPath('forms', 'sole-proprietorship'),
                name: RouteNames.FORMS_SOLE_PROPRIETORSHIP,
                component: () => import('@/core/pages/forms/enkeltmandsvirksomhed/Index.vue'),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Forms,
                },
            },
            {
                path: createPath('forms', 'aps'),
                name: RouteNames.FORMS_APS,
                component: () => import('@/core/pages/forms/aps/Index.vue'),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Forms,
                },
            },
            {
                path: createPath('forms', 'aktieselskab'),
                name: RouteNames.FORMS_AS,
                component: () => import('@/core/pages/forms/aktieselskab/Index.vue'),
                meta: {
                    allowAnonymous: true,
                    area: ShopWebsiteArea.Forms,
                },
            },
        ],
    },
    {
        path: createPath('public', 'case', ':caseId', 'identifikation', ':casePersonId'),
        name: RouteNames.CASES_CASE_IDENTIFIKATION_PUBLIC,
        component: () => import('@/pages/public/CaseIdentifikation.vue'),
        props: (route: RouteLocationNormalized) => ({
            caseId: route.params.caseId,
            casePersonId: route.params.casePersonId,
        }),
        meta: {
            allowAnonymous: true,
            area: ShopWebsiteArea.Forms,
        } as RouteMetaExtended,
    },
    {
        path: createPath('public', 'case', ':caseId', 'identifikation', 'participant', ':caseParticipantId'),
        name: RouteNames.CASES_CASE_PARTICIPANT_IDENTIFIKATION_PUBLIC,
        component: () => import('@/pages/public/CaseIdentifikation.vue'),
        props: (route: RouteLocationNormalized) => ({
            caseId: route.params.caseId,
            caseParticipantId: route.params.caseParticipantId,
        }),
        meta: {
            allowAnonymous: true,
            area: ShopWebsiteArea.Forms,
        } as RouteMetaExtended,
    },
    {
        path: '/:pathMatch(.*)*',
        component: () => import('@/pages/404.vue'),
        meta: {
            allowAnonymous: true,
        },
    },
];
